/**
 * usePlan — reads current user plan from profile and exposes gating helpers.
 * Ticket #340
 */
import { useState, useEffect } from 'react';
import { api } from '../utils/api';

export type PlanTier = 'free' | 'pro' | 'premium';

export type GatedFeature =
  | 'trip_companions'
  | 'bffs_visibility'
  | 'expiration_control'
  | 'expiration_notifications'
  | 'photo_approval'
  | 'storage_extended';

const PRO_FEATURES: GatedFeature[] = [
  'trip_companions',
  'bffs_visibility',
  'expiration_control',
  'expiration_notifications',
  'photo_approval',
  'storage_extended',
];

const PREMIUM_FEATURES: GatedFeature[] = [];

interface UsePlanReturn {
  plan: PlanTier;
  loading: boolean;
  isPro: () => boolean;
  isPremium: () => boolean;
  canUse: (feature: GatedFeature) => boolean;
  companionLimit: number; // 0 = free, 6 = pro, Infinity = premium
}

export const usePlan = (): UsePlanReturn => {
  const [plan, setPlan] = useState<PlanTier>('free');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const res = await api.get('/users/profile');
        const data = await res.json();
        if (data.success && data.profile?.currentTier) {
          const tier = data.profile.currentTier as PlanTier;
          setPlan(validTier(tier));
        }
      } catch {
        // Default to free on error
      } finally {
        setLoading(false);
      }
    };
    fetchPlan();
  }, []);

  const isPro = () => plan === 'pro' || plan === 'premium';
  const isPremium = () => plan === 'premium';

  const canUse = (feature: GatedFeature): boolean => {
    if (plan === 'premium') return true;
    if (plan === 'pro') return !PREMIUM_FEATURES.includes(feature);
    return !PRO_FEATURES.includes(feature);
  };

  const companionLimit =
    plan === 'premium' ? Infinity :
    plan === 'pro' ? 6 : 0;

  return { plan, loading, isPro, isPremium, canUse, companionLimit };
};

function validTier(t: string): PlanTier {
  if (t === 'pro' || t === 'premium') return t;
  return 'free';
}
